import { saveAs } from "file-saver";
import { useState } from "react";
import Template from "@/assets/templates/upload-bulk-bookings-template.xlsx";
import { Alert, Dropzone, Icon, Loading, Typography } from "@/components/atoms";
import { useBulkBooking } from "../BulkBookingContext";
import { ACCEPTED_FILES, MAX_FILE_SIZE, MAX_ROWS, WorkbookParseError, parseWorkbook } from "../parseWorkbook";

/**
 * Step 1 — "Upload CSV File". Takes the file, parses it, and hands the raw rows to the header picker.
 * The template download sits here too (as well as in the Book a Trip menu), since this is where
 * someone realises they don't have one.
 */
export const UploadStep = () => {
  const { loadWorkbook } = useBulkBooking();
  const [error, setError] = useState("");
  const [isParsing, setIsParsing] = useState(false);

  const handleUpload = async (file: File) => {
    setError("");
    setIsParsing(true);
    try {
      const workbook = await parseWorkbook(file);
      // Row 1 is usually headers, so the cap is on bookings rather than raw rows.
      if (workbook.rows.length > MAX_ROWS + 1) {
        setError(
          `This file contains more than ${MAX_ROWS} bookings. Split it into smaller files with up to ${MAX_ROWS} bookings each, then upload them one at a time.`
        );
        return;
      }
      loadWorkbook(file, workbook);
    } catch (e) {
      setError(e instanceof WorkbookParseError ? e.message : "We couldn't read that file. Try again.");
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* 8px either side of the label, per the design. The 20px above it is the Modal's own
          header-to-body gap, so nothing here sets it. */}
      <button
        type="button"
        onClick={() => saveAs(Template, "upload-bulk-bookings-template.xlsx")}
        className="flex w-fit items-center gap-2 text-info hover:underline"
      >
        <Icon name="excel" isCustom size="md" />
        <Typography variant="action">Download Bulk Booking Template</Typography>
        <Icon name="ImportCurve" size="md" />
      </button>

      <div className="relative">
        {isParsing && <Loading />}
        <Dropzone
          accept={ACCEPTED_FILES}
          maxSize={MAX_FILE_SIZE}
          upload={handleUpload}
          disabled={isParsing}
          // Fixed 239px per the design. Centred because Dropzone drops `detail` straight into its
          // root, so at a fixed height the content would otherwise sit against the top padding.
          // `!` because the atom's disabled branch hardcodes h-full, which would fight this while
          // a file is parsing and make the modal jump.
          className="flex !h-[239px] w-full items-center justify-center border-dashed p-10"
          detail={
            <div className="flex flex-col items-center gap-2.5">
              <Typography variant="action" className="text-neutral-silver-gray">
                Upload .xlsx, .xls or .csv file
              </Typography>
              {/* Dropzone's own click target wraps this, so it's a span styled as a button rather
                  than a nested one. Padding and height mirror Button's md size class exactly. */}
              <span className="inline-flex h-[32px] items-center rounded-md bg-primary px-3 py-1.5 text-white sm:h-[40px] sm:px-5 sm:py-2.5">
                <Typography variant="action">Select File</Typography>
              </span>
            </div>
          }
        />
        {/* The cap, stated before anyone hits it — the error above only appears once a too-large file
            has already been picked. Interpolated so both it and the error track MAX_ROWS. */}
        <Typography variant="small-regular" className="mt-2 block text-neutral-silver-gray">
          Upload up to {MAX_ROWS} bookings per file.
        </Typography>
      </div>

      {error && <Alert type="danger" message={error} />}
    </div>
  );
};
